import { useEffect, useMemo, useState } from 'react';
import {
  STABLEFORD_POINT_CATEGORIES,
  teamCupStablefordConfig,
  type StablefordConfig,
  type StablefordPointCategory,
} from '@golf-app/shared';

const LABELS: Record<StablefordPointCategory, string> = {
  albatrossOrBetter: 'Albatross or better',
  eagle: 'Eagle',
  birdie: 'Birdie',
  par: 'Par',
  bogey: 'Bogey',
  doubleBogeyOrWorse: 'Double bogey or worse',
};

type Drafts = Record<StablefordPointCategory, string> & { playerContributionLimit: string };

function draftsFor(config: StablefordConfig): Drafts {
  const fallback = teamCupStablefordConfig();
  const cap = config.teamRules?.playerContributionLimit ?? fallback.teamRules!.playerContributionLimit;
  return {
    albatrossOrBetter: String(config.points.albatrossOrBetter),
    eagle: String(config.points.eagle),
    birdie: String(config.points.birdie),
    par: String(config.points.par),
    bogey: String(config.points.bogey),
    doubleBogeyOrWorse: String(config.points.doubleBogeyOrWorse),
    playerContributionLimit: String(cap),
  };
}

function parseDrafts(drafts: Drafts): StablefordConfig | null {
  const values = STABLEFORD_POINT_CATEGORIES.map((key) => {
    const value = drafts[key];
    return /^-?\d+$/.test(value) && Number(value) >= -99 && Number(value) <= 99
      ? Number(value)
      : null;
  });
  const cap = drafts.playerContributionLimit;
  if (values.some((value) => value == null)
    || !/^\d+$/.test(cap)
    || Number(cap) > 99) return null;
  return {
    scoringBasis: 'gross',
    points: {
      albatrossOrBetter: values[0]!,
      eagle: values[1]!,
      birdie: values[2]!,
      par: values[3]!,
      bogey: values[4]!,
      doubleBogeyOrWorse: values[5]!,
    },
    teamRules: {
      targetMode: 'course_handicap',
      playerContributionLimit: Number(cap),
    },
  };
}

export function TeamStablefordSettings({
  value,
  onChange,
  onValidityChange,
}: {
  value: StablefordConfig;
  onChange: (next: StablefordConfig) => void;
  onValidityChange?: (valid: boolean) => void;
}) {
  const [drafts, setDrafts] = useState<Drafts>(() => draftsFor(value));
  const valid = useMemo(() => parseDrafts(drafts), [drafts]);

  useEffect(() => { onValidityChange?.(valid != null); }, [valid, onValidityChange]);

  function update(key: keyof Drafts, nextValue: string) {
    const next = { ...drafts, [key]: nextValue };
    setDrafts(next);
    const parsed = parseDrafts(next);
    if (parsed) onChange(parsed);
  }

  function reset() {
    const standard = teamCupStablefordConfig();
    const next = draftsFor(standard);
    setDrafts(next);
    onChange(standard);
  }

  return (
    <div className="scorecard-stableford-body">
      <p className="t-hint">
        Gross Stableford points are compared with each player’s handicap-adjusted target.
        The team total adds each player’s capped result, while the individual net board stays uncapped.
      </p>
      <div className="scorecard-points-table" aria-label="Stableford point values">
        {STABLEFORD_POINT_CATEGORIES.map((key) => {
          const pointValue = drafts[key];
          const pointValid = /^-?\d+$/.test(pointValue) && Number(pointValue) >= -99 && Number(pointValue) <= 99;
          return (
            <label className="scorecard-point-row" key={key}>
              <span>{LABELS[key]}</span>
              <input
                className={`scorecard-point-input ${pointValid ? '' : 'scorecard-point-input-error'}`}
                value={pointValue}
                onChange={(event) => update(key, event.target.value)}
                inputMode="numeric"
                maxLength={3}
                aria-label={`${LABELS[key]} point value`}
              />
            </label>
          );
        })}
        <label className="scorecard-point-row">
          <span>Player team cap</span>
          <input
            className={`scorecard-point-input ${/^\d+$/.test(drafts.playerContributionLimit) && Number(drafts.playerContributionLimit) <= 99 ? '' : 'scorecard-point-input-error'}`}
            value={drafts.playerContributionLimit}
            onChange={(event) => update('playerContributionLimit', event.target.value)}
            inputMode="numeric"
            maxLength={2}
            aria-label="Player contribution cap"
          />
        </label>
      </div>
      {!valid ? <p className="scorecard-field-error">Point values must be whole numbers from -99 to 99. The team cap must be from 0 to 99.</p> : null}
      <button type="button" className="scorecard-reset-link" onClick={reset}>Use Tyler’s defaults</button>
    </div>
  );
}
